import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import MobileStepItem from './MobileStepItem';
import MobileInsuranceCards from './MobileInsuranceCards';
import MobileOriginSelector from './MobileOriginSelector';
import MobileDestinationHybrid from './MobileDestinationHybrid';
import PhoneDialCodeSelector from './PhoneDialCodeSelector';
import { PHONE_ERROR_MESSAGE } from '../../utils/dialCodes';
import { FiShield, FiMapPin, FiCalendar, FiUsers, FiMail, FiX } from 'react-icons/fi';
import { FaCalculator } from 'react-icons/fa';
import './MobileFlow.css';

const MobileOverlay = ({
  isOpen,
  onClose,
  activeStep,
  onStepChange,
  onSearch,
  // Insurance
  selectedInsurance,
  onSelectInsurance,
  products,
  insuranceSummary,
  // Origin
  showOrigin,
  originSummary,
  originName,
  originOptions,
  selectedOrigin,
  onOriginChange,
  originConfirmed,
  onOriginConfirm,
  // Destination
  showDestination,
  destinationSummary,
  destinationOptions,
  selectedDestinations,
  onDestinationChange,
  customFilterOption,
  // Product
  showProduct,
  productSummary,
  renderProduct,
  // Dates
  showDates,
  dateSummary,
  durationSummary,
  renderDates,
  // Passengers
  passengersSummary,
  renderPassengers,
  // Contact
  showEmail,
  showPhone,
  email,
  onEmailChange,
  emailError,
  phone,
  onPhoneChange,
  phoneError,
  detectedCountryCode,
  dialCountryCode,
  onDialCountryCodeChange,
}) => {
  // Prevent body scroll (including iOS momentum scroll) when overlay opens
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        requestAnimationFrame(() => window.scrollTo(0, scrollY));
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const steps = [];

  steps.push({
    id: 'insurance',
    label: 'Selecciona tu tipo de viaje',
    icon: <FiShield />,
    summary: insuranceSummary,
    isCompleted: !!selectedInsurance,
    render: () => (
      <MobileInsuranceCards
        products={products}
        selectedInsurance={selectedInsurance}
        onSelect={(ins) => {
          if (onSelectInsurance) onSelectInsurance(ins);
          const nextId = showOrigin ? 'origin' : showDestination ? 'destination' : 'dates';
          onStepChange(nextId);
        }}
      />
    ),
  });

  if (showOrigin) {
    steps.push({
      id: 'origin',
      label: 'País de residencia',
      icon: <FiMapPin />,
      summary: originConfirmed ? originSummary : '',
      isCompleted: originConfirmed && !!originSummary,
      render: () => (
        <MobileOriginSelector
          options={originOptions || []}
          value={selectedOrigin}
          onChange={onOriginChange}
          onSelect={() => {
            if (onOriginConfirm) onOriginConfirm();
            const nextId = showDestination ? 'destination' : 'dates';
            onStepChange(nextId);
          }}
          detectedCountryCode={detectedCountryCode}
        />
      ),
    });
  }

  if (showDestination) {
    steps.push({
      id: 'destination',
      label: 'Destino',
      icon: <FiMapPin />,
      summary: destinationSummary,
      isCompleted: !!destinationSummary,
      render: () => (
        <MobileDestinationHybrid
          options={destinationOptions || []}
          value={selectedDestinations || []}
          onChange={onDestinationChange}
          customFilterOption={customFilterOption}
          originName={originName}
          onConfirm={() => onStepChange(showDates ? 'dates' : 'passengers')}
        />
      ),
    });
  }

  if (showProduct) {
    steps.push({
      id: 'product',
      label: 'Producto',
      icon: <FiMapPin />,
      summary: productSummary,
      isCompleted: !!productSummary,
      render: renderProduct,
    });
  }

  if (showDates) {
    steps.push({
      id: 'dates',
      label: 'Fechas',
      icon: <FiCalendar />,
      summary: dateSummary,
      isCompleted: !!dateSummary,
      render: () => (
        <>
          {durationSummary && <p className="sf-mobile-duration-hint">{durationSummary}</p>}
          {renderDates()}
        </>
      ),
    });
  }

  steps.push({
    id: 'passengers',
    label: 'Viajeros',
    icon: <FiUsers />,
    summary: passengersSummary,
    isCompleted: !!passengersSummary,
    render: () => (
      <>
        {renderPassengers()}
        {showEmail && (
          <button
            type="button"
            className={`sf-step-next-btn${!passengersSummary ? ' sf-step-next-btn--disabled' : ''}`}
            disabled={!passengersSummary}
            onClick={() => passengersSummary && onStepChange('email')}
          >
            Siguiente →
          </button>
        )}
      </>
    ),
  });

  if (showEmail) {
    steps.push({
      id: 'email',
      label: 'Contacto',
      icon: <FiMail />,
      summary: email || '',
      isCompleted: !!email && !emailError,
      render: () => (
        <div className="sf-fullscreen-contact">
          {showPhone && (
            <div className={`sf-mobile-contact-field ${phoneError ? 'sf-mobile-contact-field--error' : ''}`}>
              <div className="sf-mobile-contact-field__content">
                <label className="sf-mobile-contact-field__label">TELÉFONO</label>
                <PhoneDialCodeSelector
                  value={phone}
                  onChange={onPhoneChange}
                  dialCountryCode={dialCountryCode}
                  onDialCountryCodeChange={onDialCountryCodeChange}
                />
                {phoneError && (
                  <p className="sf-mobile-field-error-text">{PHONE_ERROR_MESSAGE}</p>
                )}
              </div>
            </div>
          )}
          <div className={`sf-mobile-contact-field ${emailError ? 'sf-mobile-contact-field--error' : ''}`}>
            <div className="sf-mobile-contact-field__content">
              <label className="sf-mobile-contact-field__label">EMAIL *</label>
              <input
                type="email"
                className="sf-mobile-contact-field__input"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
              />
            </div>
          </div>
          <p className="sf-mobile-consent__text">
            Al cotizar aceptas recibir comunicaciones de Segurosfly.com de acuerdo con nuestra{' '}
            <a href="https://segurosfly.com/politica-privacidad" target="_blank" rel="noopener noreferrer" className="sf-mobile-consent__link">
              Política de Privacidad
            </a>.
          </p>
        </div>
      ),
    });
  }

  const allCompleted = steps.every(step => step.isCompleted);
  const firstIncompleteStep = steps.find(step => !step.isCompleted);

  const handleCta = () => {
    if (allCompleted) {
      onSearch();
      return;
    }
    // País de residencia ya puesto (por ubicación) pero sin confirmar: «Siguiente»
    // lo confirma y avanza. Antes volvía a abrir el mismo paso y no pasaba nada
    // (Wilker, 17/10/2026): el ✓ decía «elegido» y el botón no respondía.
    if (firstIncompleteStep.id === 'origin' && selectedOrigin && originSummary) {
      if (onOriginConfirm) onOriginConfirm();
      const despues = steps.find(st => st.id !== 'origin' && st.id !== 'insurance' && !st.isCompleted);
      onStepChange(despues ? despues.id : (showDestination ? 'destination' : 'dates'));
      return;
    }
    onStepChange(firstIncompleteStep.id);
  };

  return ReactDOM.createPortal(
    <div className="sf-mobile-overlay sf-mobile-overlay--fullscreen">
      <div className="sf-mobile-sheet sf-mobile-sheet--accordion">

        <div className="sf-mobile-sheet__header sf-mobile-sheet__header--accordion">
          <span className="sf-mobile-sheet__title">Cotizar seguro</span>
          <button type="button" className="sf-mobile-sheet__close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="sf-mobile-sheet__body sf-mobile-sheet__body--accordion">
          {steps.map((step) => {
            let content;
            try {
              content = step.render();
            } catch (e) {
              console.error('[MobileOverlay] crash en step:', step.id, e);
              content = null;
            }
            return (
              <MobileStepItem
                key={step.id}
                label={step.label}
                summary={step.summary}
                isActive={activeStep === step.id}
                isCompleted={step.isCompleted}
                onToggle={() => onStepChange(activeStep === step.id ? null : step.id)}
                icon={step.icon}
              >
                {content}
              </MobileStepItem>
            );
          })}
        </div>

        <div className="sf-mobile-sheet__footer">
          <button type="button" className="sf-mobile-sheet__cta" onClick={handleCta}>
            <FaCalculator />
            <span>{allCompleted ? 'Cotizar' : 'Siguiente →'}</span>
          </button>
        </div>

      </div>
    </div>
  , document.body);
};

export default MobileOverlay;
